// One overview/landing row per layer: status + summary + ONE next action
// (spec §7: "four layer rows, each = status + ONE next action").
import type { ReactNode } from 'react';
import type { LayerKey } from '@pageboss/shared';

export type LayerStatus = 'not_started' | 'in_progress' | 'done' | 'attention';

export interface LayerRowProps {
  layer: LayerKey;
  status: LayerStatus;
  /** One line of state, e.g. "2 of 4 set up" — the setup-mode path counter. */
  summary: string;
  /** The ONE next action. Omit when the layer needs nothing. */
  action?: { label: string; href: string };
  /** Host override for client-side routing (dashboard passes a react-router
   *  <Link>); default renders a plain <a>. */
  renderAction?: (action: { label: string; href: string }) => ReactNode;
}

const STATUS_DOT: Record<LayerStatus, string> = {
  not_started: 'bg-border-strong',
  in_progress: 'bg-warning',
  done: 'bg-success',
  attention: 'bg-danger',
};

export function LayerRow({ layer, status, summary, action, renderAction }: LayerRowProps) {
  return (
    <div
      data-layer={layer}
      data-status={status}
      className="flex items-center gap-4 rounded-lg border border-border bg-surface-card px-5 py-4"
    >
      <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[status]}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-primary">{layer}</p>
        <p className="truncate text-sm text-text-secondary">{summary}</p>
      </div>
      {action
        ? renderAction
          ? renderAction(action)
          : (
              <a
                href={action.href}
                className="shrink-0 text-sm font-semibold text-brand-700 hover:text-brand-800"
              >
                {action.label} →
              </a>
            )
        : null}
    </div>
  );
}
